import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAppDataContext } from '../context/AppDataContext';
import { MessageSquare, Heart, Share2, Plus, Brain, Send } from 'lucide-react';
import type { CommunityPost } from '../types';

const TherapistCommunity: React.FC = () => {
    const { profile } = useAppDataContext();
    const [posts, setPosts] = useState<CommunityPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [showNewPost, setShowNewPost] = useState(false);
    
    // New Post Form
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [postType, setPostType] = useState<'discussion' | 'template'>('discussion');

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('community_posts')
                .select(`
                    *,
                    profiles:therapist_id (full_name, username)
                `)
                .order('created_at', { ascending: false });

            if (error) throw error;

            const formattedPosts = data?.map(post => ({
                ...post,
                therapist_name: post.profiles?.full_name || post.profiles?.username || 'מטפל אנונימי'
            })) || [];

            setPosts(formattedPosts);
        } catch (err) {
            console.error('Error fetching community posts:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePost = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!profile?.id) return;
        
        try {
            const { error } = await supabase
                .from('community_posts')
                .insert({
                    therapist_id: profile.id,
                    title,
                    content,
                    type: postType,
                });

            if (error) throw error;
            
            setShowNewPost(false);
            setTitle('');
            setContent('');
            fetchPosts();
        } catch (err) {
            console.error('Error creating post:', err);
            alert('שגיאה ביצירת הפוסט');
        }
    };

    const handleLike = async (postId: string, currentLikes: number) => {
        try {
            // Optimistic update
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: currentLikes + 1 } : p));
            
            // Server-side increment: clients may no longer update other therapists' posts.
            await supabase.rpc('increment_post_likes', { post_id: postId });
        } catch (err) {
            console.error('Error liking post:', err);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-background relative dir-rtl">
            <header className="p-6 pt-10 bg-card border-b border-border flex justify-between items-center z-10">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <MessageSquare className="text-primary" /> קהילת מטפלים
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">שתף רעיונות, שאלות ותבניות משימה</p>
                </div>
                <button
                    onClick={() => setShowNewPost(true)}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-full shadow-lg transition-transform hover:scale-105"
                >
                    <Plus size={24} />
                </button>
            </header>

            <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
                {posts.map(post => (
                    <div key={post.id} className="bg-card border border-border rounded-2xl p-5 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-muted-foreground">
                                    <Brain size={20} />
                                </div>
                                <div>
                                    <p className="text-foreground font-bold">{post.therapist_name}</p>
                                    <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString('he-IL')}</p>
                                </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-md border ${
                                post.type === 'template' 
                                ? 'bg-primary/10 text-primary border-primary/20' 
                                : 'bg-muted text-muted-foreground border-border'
                            }`}>
                                {post.type === 'template' ? 'תבנית משימה' : 'דיון'}
                            </span>
                        </div>

                        <h3 className="text-lg font-bold text-foreground mb-2">{post.title}</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-4">{post.content}</p>

                        <div className="flex items-center gap-4 pt-4 border-t border-border">
                            <button 
                                onClick={() => handleLike(post.id, post.likes_count)}
                                className="flex items-center gap-1.5 text-muted-foreground hover:text-pink-500 transition-colors"
                            >
                                <Heart size={18} />
                                <span className="text-sm">{post.likes_count}</span>
                            </button>
                            <button className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors">
                                <Share2 size={18} />
                                <span className="text-sm">שתף</span>
                            </button>
                        </div>
                    </div>
                ))}

                {posts.length === 0 && (
                    <div className="text-center py-20">
                        <MessageSquare size={48} className="mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-xl font-bold text-muted-foreground">עדיין אין פוסטים בקהילה</h3>
                        <p className="text-muted-foreground mt-2">תהיה הראשון לשתף!</p>
                    </div>
                )}
            </div>

            {/* New Post Modal */}
            {showNewPost && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4">
                    <div className="bg-card border border-border rounded-3xl w-full max-w-md p-6 relative">
                        <h2 className="text-xl font-bold text-foreground mb-4">פוסט חדש לקהילה</h2>
                        <form onSubmit={handleCreatePost} className="space-y-4">
                            
                            <div className="flex gap-2 p-1 bg-muted rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => setPostType('discussion')}
                                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${postType === 'discussion' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-white'}`}
                                >
                                    דיון / שאלה
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPostType('template')}
                                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${postType === 'template' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-white'}`}
                                >
                                    תבנית משימה
                                </button>
                            </div>

                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="כותרת הפוסט..."
                                className="w-full bg-muted border border-border rounded-xl p-3 text-foreground focus:outline-none focus:border-primary"
                                required
                            />
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="שתף את המחשבות שלך או תאר את המשימה..."
                                className="w-full bg-muted border border-border rounded-xl p-3 text-foreground h-32 resize-none focus:outline-none focus:border-primary"
                                required
                            />

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowNewPost(false)}
                                    className="flex-1 bg-muted hover:bg-muted text-foreground py-3 rounded-xl font-bold transition-colors"
                                >
                                    ביטול
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground py-3 rounded-xl font-bold transition-colors flex justify-center items-center gap-2"
                                >
                                    <Send size={18} /> פרסם
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TherapistCommunity;
